<template>
  <div>
    <Breadcrumbs main="Accounts" title="Invoices" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">Invoices</h5>
                <span class="text-muted">Manage client invoices and payment status</span>
              </div>
              <div class="d-flex gap-2">
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  :disabled="loading"
                  @click="loadInvoices"
                >
                  <vue-feather type="refresh-cw" class="me-1" />
                  Refresh
                </button>
                <button
                  v-if="canAdd"
                  class="btn btn-primary"
                  type="button"
                  @click="openCreate"
                >
                  <vue-feather type="plus" class="me-1" />
                  Add Invoice
                </button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="error" class="alert alert-danger" role="alert">
                {{ error }}
              </div>
              <div v-if="loading" class="text-center py-4 text-muted">
                Loading invoices...
              </div>
              <div v-else-if="invoices.length === 0" class="text-center py-4 text-muted">
                No invoices found.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>Invoice No.</th>
                      <th>Client</th>
                      <th>Matter</th>
                      <th class="text-end">Amount</th>
                      <th class="text-end">Paid</th>
                      <th class="text-end">Balance</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="invoice in invoices" :key="invoice.invoiceId">
                      <td>
                        <strong>{{ invoice.invoiceNo || `#${invoice.invoiceId}` }}</strong>
                      </td>
                      <td>{{ clientName(invoice.clientId) }}</td>
                      <td>{{ matterLabel(invoice.matterId) }}</td>
                      <td class="text-end">{{ formatMoney(invoice.amount) }}</td>
                      <td class="text-end">{{ formatMoney(invoice.amountPaid) }}</td>
                      <td class="text-end">{{ formatMoney(invoice.balance) }}</td>
                      <td>
                        <span class="badge" :class="paymentStatusBadgeClass(invoice.paymentStatus)">
                          {{ invoice.paymentStatus || '—' }}
                        </span>
                      </td>
                      <td>{{ formatDate(invoice.createdAt) }}</td>
                      <td class="text-end">
                        <button
                          v-if="canEdit"
                          class="btn btn-sm btn-outline-primary me-1"
                          type="button"
                          title="Edit"
                          @click="openEdit(invoice)"
                        >
                          <vue-feather type="edit-2" />
                        </button>
                        <button
                          v-if="canDelete"
                          class="btn btn-sm btn-outline-danger"
                          type="button"
                          title="Delete"
                          @click="confirmDelete(invoice)"
                        >
                          <vue-feather type="trash-2" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="showFormModal"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
          <div class="modal-content">
            <form @submit.prevent="submitForm">
              <div class="modal-header">
                <h5 class="modal-title">{{ isEditing ? 'Edit Invoice' : 'Add Invoice' }}</h5>
                <button type="button" class="btn-close" aria-label="Close" @click="closeFormModal" />
              </div>
              <div class="modal-body">
                <div v-if="formError" class="alert alert-danger" role="alert">
                  {{ formError }}
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Invoice number</label>
                      <input
                        v-model="form.invoiceNo"
                        class="form-control"
                        type="text"
                        maxlength="100"
                        placeholder="e.g. INV-2026-001"
                      >
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Client <span class="text-danger">*</span></label>
                      <select
                        v-model.number="form.clientId"
                        class="form-select"
                        :required="!isEditing"
                        @change="onClientChange"
                      >
                        <option :value="null" disabled>Select a client</option>
                        <option
                          v-for="client in clients"
                          :key="client.clientId"
                          :value="client.clientId"
                        >
                          {{ client.fullName || `Client #${client.clientId}` }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Matter</label>
                      <select v-model.number="form.matterId" class="form-select">
                        <option :value="null">None</option>
                        <option
                          v-for="matter in filteredMatters"
                          :key="matter.matterId"
                          :value="matter.matterId"
                        >
                          {{ matterLabel(matter.matterId) }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Amount</label>
                      <input
                        v-model.number="form.amount"
                        class="form-control"
                        type="number"
                        min="0"
                        step="0.01"
                      >
                    </div>
                  </div>
                  <div v-if="isEditing" class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Amount paid</label>
                      <input
                        v-model.number="form.amountPaid"
                        class="form-control"
                        type="number"
                        min="0"
                        step="0.01"
                      >
                    </div>
                  </div>
                  <div v-if="isEditing" class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Payment status</label>
                      <select v-model="form.paymentStatus" class="form-select">
                        <option value="">Auto (from amount paid)</option>
                        <option v-for="s in paymentStatusOptions" :key="s" :value="s">{{ s }}</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Description</label>
                      <textarea v-model="form.description" class="form-control" rows="3" />
                    </div>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeFormModal">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  {{ saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Invoice') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div v-if="showFormModal" class="modal-backdrop fade show" @click="closeFormModal" />
    </teleport>

    <teleport to="body">
      <div
        v-if="invoiceToDelete"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Delete Invoice</h5>
              <button type="button" class="btn-close" aria-label="Close" @click="invoiceToDelete = null" />
            </div>
            <div class="modal-body">
              <p class="mb-2">
                Delete invoice
                <strong>{{ invoiceToDelete.invoiceNo || `#${invoiceToDelete.invoiceId}` }}</strong>?
              </p>
              <p class="text-muted mb-0 f-12">
                The invoice will be soft-deleted and hidden from the list. The record is retained for audit.
              </p>
              <div v-if="actionError" class="alert alert-danger mt-3 mb-0" role="alert">
                {{ actionError }}
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="invoiceToDelete = null">
                Cancel
              </button>
              <button type="button" class="btn btn-danger" :disabled="actionLoading" @click="submitDelete">
                {{ actionLoading ? 'Deleting...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="invoiceToDelete" class="modal-backdrop fade show" @click="invoiceToDelete = null" />
    </teleport>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { listClients } from '@/services/clientService'
import { listMatters } from '@/services/matterService'
import {
  listInvoices,
  createInvoice,
  updateInvoice,
  deleteInvoice,
} from '@/services/invoiceService'

const PAYMENT_STATUS_OPTIONS = ['UNPAID', 'PARTIAL', 'PAID']

function hasInvoicePrivilege(user, action) {
  if (!user?.privileges?.length) return true
  const invoicePriv = user.privileges.find((p) => p.entity === 'INVOICE')
  if (!invoicePriv) return false
  const map = {
    view: 'canView',
    add: 'canAdd',
    edit: 'canEdit',
    delete: 'canDelete',
  }
  return invoicePriv[map[action]] === true
}

const emptyForm = () => ({
  invoiceNo: '',
  clientId: null,
  matterId: null,
  amount: null,
  amountPaid: null,
  paymentStatus: '',
  description: '',
})

export default {
  name: 'AccountsInvoices',

  computed: {
    ...mapGetters('auth', ['currentUser']),

    canAdd() {
      return hasInvoicePrivilege(this.currentUser, 'add')
    },

    canEdit() {
      return hasInvoicePrivilege(this.currentUser, 'edit')
    },

    canDelete() {
      return hasInvoicePrivilege(this.currentUser, 'delete')
    },

    paymentStatusOptions() {
      return PAYMENT_STATUS_OPTIONS
    },

    filteredMatters() {
      if (!this.form.clientId) return []
      return this.matters.filter((m) => m.clientId === this.form.clientId)
    },
  },

  data() {
    return {
      invoices: [],
      clients: [],
      matters: [],
      loading: false,
      error: '',
      showFormModal: false,
      isEditing: false,
      editingId: null,
      originalMatterId: null,
      form: emptyForm(),
      formError: '',
      saving: false,
      invoiceToDelete: null,
      actionError: '',
      actionLoading: false,
    }
  },

  created() {
    this.loadInvoices()
    this.loadClients()
    this.loadMatters()
  },

  methods: {
    async loadInvoices() {
      this.loading = true
      this.error = ''

      try {
        const data = await listInvoices()
        this.invoices = Array.isArray(data) ? data : []
      } catch (err) {
        if (err.status === 403) {
          this.error = 'You do not have permission to view invoices.'
        } else {
          this.error = err.message || 'Failed to load invoices'
        }
      } finally {
        this.loading = false
      }
    },

    async loadClients() {
      try {
        const data = await listClients()
        this.clients = Array.isArray(data) ? data : []
      } catch {
        this.clients = []
      }
    },

    async loadMatters() {
      try {
        const data = await listMatters()
        this.matters = Array.isArray(data) ? data : []
      } catch {
        this.matters = []
      }
    },

    clientName(clientId) {
      const client = this.clients.find((c) => c.clientId === clientId)
      return client?.fullName || `#${clientId}`
    },

    matterLabel(matterId) {
      if (!matterId) return '—'
      const matter = this.matters.find((m) => m.matterId === matterId)
      if (!matter) return `#${matterId}`
      return matter.caseTitle || matter.caseNo || `Matter #${matterId}`
    },

    paymentStatusBadgeClass(status) {
      const normalized = (status || '').toUpperCase()
      if (normalized === 'PAID') return 'badge-success'
      if (normalized === 'PARTIAL') return 'badge-warning'
      if (normalized === 'UNPAID') return 'badge-danger'
      return 'badge-light-primary'
    },

    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString()
    },

    formatMoney(value) {
      if (value == null || Number.isNaN(Number(value))) return '—'
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    onClientChange() {
      if (!this.form.matterId) return
      const matter = this.matters.find((m) => m.matterId === this.form.matterId)
      if (!matter || matter.clientId !== this.form.clientId) {
        this.form.matterId = null
      }
    },

    openCreate() {
      this.isEditing = false
      this.editingId = null
      this.originalMatterId = null
      this.form = emptyForm()
      this.formError = ''
      this.showFormModal = true
    },

    openEdit(invoice) {
      this.isEditing = true
      this.editingId = invoice.invoiceId
      this.originalMatterId = invoice.matterId || null
      this.form = {
        invoiceNo: invoice.invoiceNo || '',
        clientId: invoice.clientId,
        matterId: invoice.matterId || null,
        amount: invoice.amount ?? null,
        amountPaid: invoice.amountPaid ?? null,
        paymentStatus: invoice.paymentStatus || '',
        description: invoice.description || '',
      }
      this.formError = ''
      this.showFormModal = true
    },

    closeFormModal() {
      if (this.saving) return
      this.showFormModal = false
      this.formError = ''
    },

    buildPayload() {
      const payload = {}

      const invoiceNo = this.form.invoiceNo?.trim()
      if (invoiceNo) payload.invoiceNo = invoiceNo

      if (this.form.clientId) payload.clientId = this.form.clientId

      const currentMatterId = this.form.matterId || null
      if (this.isEditing) {
        if (currentMatterId !== this.originalMatterId) {
          payload.matterId = currentMatterId
        }
      } else if (currentMatterId) {
        payload.matterId = currentMatterId
      }

      if (this.form.amount != null && this.form.amount !== '') {
        payload.amount = Number(this.form.amount)
      }

      if (this.isEditing && this.form.amountPaid != null && this.form.amountPaid !== '') {
        payload.amountPaid = Number(this.form.amountPaid)
      }

      const description = this.form.description?.trim()
      if (description) payload.description = description

      if (this.isEditing && this.form.paymentStatus) {
        payload.paymentStatus = this.form.paymentStatus
      }

      return payload
    },

    async submitForm() {
      this.formError = ''

      if (!this.isEditing && !this.form.clientId) {
        this.formError = 'Please select a client.'
        return
      }

      this.saving = true

      try {
        if (this.isEditing) {
          await updateInvoice({
            invoiceId: this.editingId,
            ...this.buildPayload(),
          })
        } else {
          await createInvoice(this.buildPayload())
        }
        this.showFormModal = false
        await this.loadInvoices()
      } catch (err) {
        this.formError = err.message || 'Request failed'
      } finally {
        this.saving = false
      }
    },

    confirmDelete(invoice) {
      this.invoiceToDelete = invoice
      this.actionError = ''
    },

    async submitDelete() {
      this.actionError = ''
      this.actionLoading = true

      try {
        await deleteInvoice(this.invoiceToDelete.invoiceId)
        this.invoiceToDelete = null
        await this.loadInvoices()
      } catch (err) {
        this.actionError = err.message || 'Failed to delete invoice'
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>
